/* ──────────────────────────────────────────────────────────────────────
 * DuplicateClientCard
 *
 * Premium, compact awareness card rendered when the real-time duplicate
 * check finds an existing client or lead. NOT a validation error — the
 * field itself stays in its normal state; this card sits beneath it as
 * an informational notice the operator can act on (open the existing
 * record) or ignore (proceed with creation).
 *
 * Visual spec (from the product design):
 *   • soft red ground (#FFF5F5) with a subtle red hairline border
 *   • triangle warning icon, properly aligned
 *   • title in bold, client name in semi-bold below
 *   • "N records found" hint when matches > 1
 *   • single "View Client" / "View Lead" link button, opens in new tab
 *
 * Disappears automatically when the input no longer resolves to a
 * match — consumer controls the `open` boolean via the hook's state.
 * ──────────────────────────────────────────────────────────────────── */

import React from "react";
import { Box, Collapse, Stack, Typography, Button } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { DuplicateCheckResult } from "./leadApi";
import { duplicateProfileHref } from "./useLeadDuplicateCheck";

interface Props {
  /** The match payload from useLeadDuplicateCheck. Null hides the card. */
  match: DuplicateCheckResult | null;
  /** Optional override for the deep-link target (rare). */
  href?: string | null;
  /** Tighter variant for narrow grid items (e.g. NewPopUpCustomer). */
  dense?: boolean;
  /** True when the modal has auto-selected this client in the Select
   *  Client dropdown. Swaps the icon to a success check + replaces the
   *  bottom hint with "Client automatically selected". */
  autoLinked?: boolean;
  /** True when the duplicate detector found >1 matching client. We
   *  show a softer "Multiple matching clients" subtitle and suppress
   *  the View link (the operator needs to pick the right one). */
  ambiguous?: boolean;
}

/**
 * Renders nothing when `match` is null/undefined — safe to mount
 * unconditionally inside a Collapse wrapper.
 */
export default function DuplicateClientCard({
  match,
  href,
  dense = false,
  autoLinked = false,
  ambiguous = false,
}: Props) {
  const visible = !!match;
  const linkTarget = match ? (href ?? duplicateProfileHref(match)) : null;

  // Three visual modes, picked in priority order:
  //   • auto-linked → success-toned (green ground, check icon)
  //   • ambiguous → soft-warning (default red ground but no View link)
  //   • plain warning → red ground + View link
  const successMode = autoLinked && match?.type === "client";
  const ground      = successMode ? "#F0FDF4" : "#FFF5F5";
  const stroke      = successMode ? "#BBF7D0" : "#FECACA";
  const titleColor  = successMode ? "#166534" : "#991b1b";
  const iconColor   = successMode ? "#16a34a" : "#dc2626";

  // Title + bottom hint per mode. Keep titles short — the bold name
  // line beneath is what the operator reads first.
  const title = match?.type === "lead"
    ? "Existing Lead Found"
    : "Existing Client Found";

  const hint = (() => {
    if (!match) return null;
    if (successMode)   return "Client automatically selected";
    if (ambiguous)     return "Multiple matching clients found — pick one from the dropdown";
    if ((match.matches ?? 1) > 1) return `${match.matches} existing records found`;
    return null;
  })();

  return (
    <Collapse in={visible} timeout={180} unmountOnExit>
      {match && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            mt: dense ? 0.75 : 1,
            px: dense ? 1.2 : 1.4,
            py: dense ? 0.8 : 1,
            borderRadius: "10px",
            bgcolor: ground,
            border: `1px solid ${stroke}`,
            boxShadow: successMode
              ? "0 1px 2px rgba(22,163,74,0.05)"
              : "0 1px 2px rgba(220,38,38,0.04)",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.1,
          }}
        >
          {successMode ? (
            <CheckCircleRoundedIcon
              sx={{ fontSize: dense ? 16 : 18, color: iconColor, mt: "1px", flexShrink: 0 }}
            />
          ) : (
            <WarningAmberRoundedIcon
              sx={{ fontSize: dense ? 16 : 18, color: iconColor, mt: "1px", flexShrink: 0 }}
            />
          )}

          <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.1}>
            <Typography sx={{
              fontSize: dense ? 10.5 : 11,
              fontWeight: 700,
              color: titleColor,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              lineHeight: 1.2,
            }}>
              {title}
            </Typography>
            <Typography sx={{
              fontSize: dense ? 12 : 13,
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}>
              {match.name || "Unnamed record"}
            </Typography>
            {hint && (
              <Typography sx={{
                fontSize: 10.5,
                color: successMode ? "#15803d" : "#7c1d1d",
                lineHeight: 1.3,
                fontWeight: 500,
              }}>
                {hint}
              </Typography>
            )}
          </Stack>

          {/* Hide the View link when the dropdown is the right action
              (ambiguous match) — sending the operator to a single
              record's profile would be misleading. */}
          {linkTarget && !ambiguous && (
            <Button
              href={linkTarget}
              target="_blank"
              rel="noreferrer"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 11,
                color: successMode ? "#15803d" : "#dc2626",
                borderRadius: "6px",
                px: 1.2,
                py: 0.3,
                minWidth: 0,
                alignSelf: "center",
                flexShrink: 0,
                "&:hover": {
                  bgcolor: successMode ? "rgba(22,163,74,0.08)" : "rgba(220,38,38,0.08)",
                },
              }}
            >
              {match.type === "lead" ? "View Lead" : "View Client"}
            </Button>
          )}
        </Box>
      )}
    </Collapse>
  );
}

/**
 * Picks the "primary" match when both phone + email return a hit.
 * Rules (highest priority first):
 *   1. If both fields resolved to the SAME record, return it once.
 *   2. Prefer a `client` match over a `lead` match.
 *   3. Prefer the phone match over the email match.
 * Returns null when neither field matched anything.
 */
export function pickPrimaryDuplicate(
  phoneMatch: DuplicateCheckResult | null,
  emailMatch: DuplicateCheckResult | null,
): DuplicateCheckResult | null {
  if (!phoneMatch && !emailMatch) return null;
  if (phoneMatch && emailMatch && phoneMatch.id === emailMatch.id && phoneMatch.type === emailMatch.type) {
    return phoneMatch;
  }
  if (phoneMatch?.type === "client") return phoneMatch;
  if (emailMatch?.type === "client") return emailMatch;
  return phoneMatch || emailMatch;
}
